import React, { useMemo } from 'react';
import { PieChart, Pie, Cell, ResponsiveContainer } from 'recharts';
import { getSentimentColor, getStatusColor } from '@t-one-internal/design-system';

// Support both snake_case (API) and camelCase formats
interface SentimentRow {
  segment?: string;
  segment_count?: number;
  segmentCount?: number;
  segment_pct?: number;
  segmentPct?: number;
  enps_score?: number;
  enpsScore?: number;
  total_responses?: number;
  totalResponses?: number;
  survey_title?: string;
  surveyTitle?: string;
}

interface SurveyOption {
  value: string;
  label: string;
}

interface ENPSSentimentWidgetProps {
  data?: SentimentRow | SentimentRow[] | { data: SentimentRow[] };
  loading?: boolean;
  error?: string;
  surveyOptions?: SurveyOption[];
  selectedSurveyId?: string;
  onSurveyChange?: (surveyId: string) => void;
  surveyOptionsLoading?: boolean;
}

const extractDataArray = (
  data: ENPSSentimentWidgetProps['data']
): SentimentRow[] => {
  if (!data) return [];
  if (Array.isArray(data)) return data;
  if ((data as { data: SentimentRow[] })?.data) {
    return (data as { data: SentimentRow[] }).data;
  }
  return [data as SentimentRow];
};

const getSegment = (d: SentimentRow): string => d.segment ?? '';
const getCount = (d: SentimentRow): number =>
  Number(d.segmentCount ?? d.segment_count ?? 0);
const getPct = (d: SentimentRow): number =>
  Number(d.segmentPct ?? d.segment_pct ?? 0);
const getEnpsScore = (d: SentimentRow): number =>
  Number(d.enpsScore ?? d.enps_score ?? 0);
const getTotalResponses = (d: SentimentRow): number =>
  Number(d.totalResponses ?? d.total_responses ?? 0);

const SEGMENT_CONFIG: Record<string, { color: string; dotClass: string; textClass: string }> = {
  Promoters:  { color: getSentimentColor('positive'), dotClass: 'bg-emerald-500', textClass: 'text-emerald-600' },
  Passives:   { color: getStatusColor('pending'), dotClass: 'bg-amber-500', textClass: 'text-amber-600' },
  Detractors: { color: getSentimentColor('negative'), dotClass: 'bg-red-500', textClass: 'text-red-600' },
};

const getScoreColor = (score: number): string => {
  if (score >= 50) return 'text-emerald-600';
  if (score >= 0) return 'text-amber-600';
  return 'text-red-600';
};

export const EngagementPulseRadarWidget: React.FC<ENPSSentimentWidgetProps> = ({
  data,
  loading,
  error,
  surveyOptions,
  selectedSurveyId,
  onSurveyChange,
  surveyOptionsLoading,
}) => {
  const { chartData, enpsScore, totalResponses } = useMemo(() => {
    const rawData = extractDataArray(data);
    if (!rawData.length) return { chartData: [], enpsScore: 0, totalResponses: 0 };

    const chartData = rawData.map((d) => ({
      name: getSegment(d),
      value: getCount(d),
      pct: getPct(d),
      color: SEGMENT_CONFIG[getSegment(d)]?.color || '#9CA3AF',
    }));

    return {
      chartData,
      enpsScore: getEnpsScore(rawData[0]),
      totalResponses: getTotalResponses(rawData[0]),
    };
  }, [data]);

  const headerEl = (
    <div className="flex items-center justify-between mb-4">
      <div className="flex items-center gap-2">
        <h3 className="font-semibold text-gray-900 text-sm">eNPS Sentiment</h3>
        <span className="text-[10px] px-1.5 py-0.5 rounded font-medium bg-indigo-100 text-indigo-700">Survey</span>
      </div>
      {surveyOptions && surveyOptions.length > 0 && onSurveyChange ? (
        <select
          value={selectedSurveyId || ''}
          onChange={(e) => onSurveyChange(e.target.value)}
          disabled={surveyOptionsLoading}
          className="text-xs border border-gray-200 rounded-md px-2 py-1 text-gray-600 bg-white focus:outline-none focus:ring-1 focus:ring-indigo-400 w-[200px] truncate"
        >
          {surveyOptionsLoading ? (
            <option>Loading...</option>
          ) : (
            surveyOptions.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))
          )}
        </select>
      ) : null}
    </div>
  );

  if (loading) {
    return (
      <div className="p-5 h-full flex flex-col">
        {headerEl}
        <div className="flex-1 flex items-center justify-center">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-indigo-600" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-5 h-full flex flex-col">
        {headerEl}
        <div className="flex-1 flex items-center justify-center text-red-500 text-sm">
          {error}
        </div>
      </div>
    );
  }

  if (chartData.length === 0) {
    return (
      <div className="p-5 h-full flex flex-col">
        {headerEl}
        <div className="flex-1 flex flex-col items-center justify-center text-center px-4 gap-2">
          <span className="text-gray-400 text-sm">No eNPS survey data yet</span>
          <span className="text-gray-400 text-xs">
            Create and run an eNPS survey to see the sentiment breakdown.
          </span>
        </div>
      </div>
    );
  }

  return (
    <div className="p-5 h-full flex flex-col">
      {headerEl}

      {/* Donut chart with eNPS score in center + Legend */}
      <div className="flex-1 flex items-center gap-6">
        <div className="relative" style={{ width: 160, height: 160 }}>
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={chartData}
                cx="50%"
                cy="50%"
                innerRadius={45}
                outerRadius={72}
                dataKey="value"
                strokeWidth={0}
              >
                {chartData.map((entry, i) => (
                  <Cell key={i} fill={entry.color} />
                ))}
              </Pie>
            </PieChart>
          </ResponsiveContainer>
          {/* Center eNPS score */}
          <div
            className="absolute inset-0 flex flex-col items-center justify-center"
            style={{ pointerEvents: 'none' }}
          >
            <div className={`text-2xl font-bold ${getScoreColor(enpsScore)}`}>
              {enpsScore > 0 ? '+' : ''}{Math.round(enpsScore)}
            </div>
            <div className="text-[10px] text-gray-400">eNPS</div>
          </div>
        </div>

        {/* Legend */}
        <div className="flex-1 space-y-3">
          {chartData.map((entry) => {
            const config = SEGMENT_CONFIG[entry.name];
            return (
              <div key={entry.name} className="flex items-center gap-3">
                <div className={`w-3 h-3 rounded-full ${config?.dotClass || 'bg-gray-400'}`} />
                <div className="flex-1">
                  <div className="text-sm font-medium text-gray-900">{entry.name}</div>
                  <div className="text-[11px] text-gray-400">{entry.value} responses</div>
                </div>
                <span className={`text-sm font-bold ${config?.textClass || 'text-gray-600'}`}>
                  {entry.pct}%
                </span>
              </div>
            );
          })}
          <div className="pt-2 border-t border-gray-100">
            <div className="text-[11px] text-gray-400">{totalResponses} total responses</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EngagementPulseRadarWidget;
